import { z } from 'zod';
import { validateQueryParamValue } from '@utils/validators';
import language_codes_raw from '@/assets/csv/language_codes.csv';
import { find } from 'lodash';

const language_codes = language_codes_raw as { alpha2: string }[];

export const tagSchema = z.object({
  tags: z.string().min(1, 'Tag name is required'),
});

export type TagSchema = z.infer<typeof tagSchema>;

export const keywordSchema = z.object({
  keyword: z.string().min(1, 'Keyword is required'),
  tags: z.record(z.string().length(20, 'Provided incorrect tag id'), z.boolean()),
  language: z
    .string()
    .refine((val) => !!find(language_codes, { alpha2: val.toLowerCase() }), {
      message: 'Provided language does not match known languages',
    }),
  countryCode: z
    .string()
    .min(3, 'Country code must be at least 3 digits long')
    .max(4, 'Country code digits length must not be greater than 4'),
});

export type KeywordSchema = z.infer<typeof keywordSchema>;

export const newWorkspaceSchema = z.object({
  workspaceName: z.string().min(3, 'Workspace name must be at least 3 characters long'),
});

export type NewWorkspaceSchema = z.infer<typeof newWorkspaceSchema>;

export const keywordCSVSchema = z
  .object({
    keyword: z.string().min(1, 'Keyword is required'),
    tags: z.string().min(1, 'Tags are required'),
    language: z
      .string()
      .refine((val) => !!find(language_codes, { alpha2: val.toLowerCase() }), {
        message: 'Provided language does not match known languages',
      }),
  })
  .array();

export type KeywordCSVSchema = z.infer<typeof keywordCSVSchema>;

export const keywordsCSVTagsSchema = z
  .string({
    invalid_type_error: "Tags doesn't match database",
    required_error: "Tags doesn't match database",
  })
  .min(1, 'Tags are required')
  .array();

export type KeywordsCSVTagsSchema = z.infer<typeof keywordsCSVTagsSchema>;

export const taxonomyFileSchema = z.object({
  file: z
    .array(
      z.object({
        parameterName: z
          .string({
            invalid_type_error: 'Parameter Name is required',
            required_error: 'Parameter Name is required',
          })
          .min(1, 'Parameter Name is required'),
        queryParameter: z
          .string({
            invalid_type_error: 'Query Parameter is required',
            required_error: 'Query Parameter is required',
          })
          .min(1, 'Query Parameter is required')
          .refine(validateQueryParamValue, { message: 'Invalid Query Parameter' }),
        prefix: z.string().nullable(),
        description: z
          .string({
            invalid_type_error: 'Description is required',
            required_error: 'Description is required',
          })
          .min(1, 'Description is required'),
      }),
    )
    .min(1, 'Valid CSV file must be provided')
    .superRefine((val, ctx) => {
      const groupedParams: Record<string, (string | null)[]> = {};
      val.forEach((taxo) => {
        if (!groupedParams[taxo.queryParameter]) {
          groupedParams[taxo.queryParameter] = [taxo.prefix];
        } else {
          groupedParams[taxo.queryParameter].push(taxo.prefix);
        }
      });
      Object.entries(groupedParams).forEach(([queryParameter, prefixes]) => {
        const nullPrefixIndex = prefixes.findIndex((prefix) => prefix === null);
        if (nullPrefixIndex !== -1 && prefixes.length > 1) {
          ctx.addIssue({
            message: `Query parameter '${queryParameter}' has null and other prefixes`,
            code: z.ZodIssueCode.custom,
          });
        }
      });
    }),
});

export type TaxonomyFileFormData = z.infer<typeof taxonomyFileSchema>;
